import { useId, useLayoutEffect, useRef, useState } from "react";
import { User } from "lucide-react";
import type { AgentStudentApplication } from "@/types/user";

type StudentSnapshot = Pick<
  AgentStudentApplication,
  | "preferredTitle"
  | "firstName"
  | "middleName"
  | "lastName"
  | "dateOfBirth"
  | "gender"
  | "maritalStatus"
  | "nsn"
  | "countryOfCitizenship"
  | "passportExpiryDate"
  | "ethnicity"
  | "iwi"
  | "firstLanguage"
  | "email"
  | "priorActivity"
  | "disability"
  | "disabilityDetails"
  | "convictionChecks"
  | "convictionDetails"
>;

/** The personal information from this application snapshot, without another lookup. */
export function StudentSummary({
  student,
  formatDate,
}: {
  student: StudentSnapshot;
  formatDate: (date: string | Date | null | undefined) => string;
}) {
  const headingId = useId();
  const summaryRef = useRef<HTMLElement>(null);
  const identityRef = useRef<HTMLDivElement>(null);
  const detailsRef = useRef<HTMLDivElement>(null);
  const fieldsRef = useRef<HTMLDivElement>(null);
  const [scrollable, setScrollable] = useState(false);
  const [canStick, setCanStick] = useState(true);
  const fullName = [
    student.preferredTitle,
    student.firstName,
    student.middleName,
    student.lastName,
  ]
    .filter(Boolean)
    .join(" ");

  useLayoutEffect(() => {
    const details = detailsRef.current;
    const fields = fieldsRef.current;
    const identity = identityRef.current;
    const shell = summaryRef.current?.closest(".application-details-shell");
    const header = shell?.querySelector("header");
    if (!details || !fields || !identity) return;
    const measure = () => {
      const headerHeight = shell
        ? parseFloat(
            window
              .getComputedStyle(shell)
              .getPropertyValue("--application-header-height"),
          ) || 80
        : 80;
      const availableHeight =
        (window.visualViewport?.height || window.innerHeight) -
        headerHeight -
        32;
      // A very long identity must not consume the entire scroll body on short
      // desktop viewports. In that case all fields stay in the document flow.
      const fits =
        availableHeight >= identity.getBoundingClientRect().height + 160;
      setCanStick(fits);
      setScrollable(fits && details.scrollHeight > details.clientHeight + 1);
    };
    measure();
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    observer?.observe(details);
    observer?.observe(fields);
    observer?.observe(identity);
    if (header) observer?.observe(header);
    window.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("resize", measure);
    };
  }, [student]);

  const groups = [
    [
      student.dateOfBirth && ["Date of birth", formatDate(student.dateOfBirth)],
      student.gender && ["Gender", student.gender],
      student.maritalStatus && ["Marital status", student.maritalStatus],
      student.nsn && ["NSN", student.nsn],
    ],
    [
      student.countryOfCitizenship && [
        "Country of citizenship",
        student.countryOfCitizenship,
      ],
      student.passportExpiryDate && [
        "Passport expiry date",
        formatDate(student.passportExpiryDate),
      ],
    ],
    [
      student.ethnicity && ["Ethnicity", student.ethnicity],
      student.firstLanguage && ["First language", student.firstLanguage],
      student.iwi && ["Iwi", student.iwi],
      student.email && ["Email", student.email],
    ],
    [
      student.priorActivity && ["Prior activity", student.priorActivity],
      student.disability && [
        "Disability",
        `Yes${student.disabilityDetails ? ` - ${student.disabilityDetails}` : ""}`,
      ],
      student.convictionChecks && [
        "Conviction checks",
        `Yes${student.convictionDetails ? ` - ${student.convictionDetails}` : ""}`,
      ],
    ],
  ]
    .map((group) =>
      group.filter((field): field is string[] => Array.isArray(field)),
    )
    .filter((group) => group.length);
  const pairedFields = new Set([
    "Date of birth",
    "Gender",
    "Marital status",
    "NSN",
    "Ethnicity",
    "First language",
  ]);
  const narrativeFields = new Set([
    "Prior activity",
    "Disability",
    "Conviction checks",
  ]);

  return (
    <aside
      ref={summaryRef}
      className="application-student-summary"
      aria-labelledby={headingId}
      data-stickable={canStick}
    >
      <div ref={identityRef} className="application-student-summary__identity">
        <p className="application-student-summary__eyebrow">
          <User size={15} aria-hidden="true" />
          Student summary
        </p>
        <h2 id={headingId}>{fullName}</h2>
      </div>
      <div
        ref={detailsRef}
        className="application-student-summary__details"
        role="region"
        aria-label={`Personal details for ${fullName}`}
        tabIndex={scrollable ? 0 : undefined}
      >
        <div ref={fieldsRef} className="application-student-summary__fields">
          {groups.map((group) => (
            <dl
              key={group[0][0]}
              className="application-student-summary__group"
            >
              {group.map(([label, value]) => (
                <div
                  key={label}
                  className={`application-student-summary__field${pairedFields.has(label) ? "" : " application-student-summary__field--full"}${narrativeFields.has(label) ? " application-student-summary__field--narrative" : ""}`}
                >
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          ))}
        </div>
      </div>
    </aside>
  );
}
